import config from "config";
import {
  IDToken,
  OktaAuth,
  SigninWithRedirectOptions,
} from "@okta/okta-auth-js";

import { routes } from "services/routes";

interface LoginProps {
  oktaAuth: OktaAuth;
  redirect?: string;
}

/**
 * If singleIDPLogin tenant => immediately Sign in via Okta <=> pre-configured IDP
 * If not a singleIDPLogin tenant => taken to Viaduct Okta Sign-in page (https://viaduct-cloud.okta.com),
 * where they log in without going to the IDP login at all.
 */
const MAX_RETRIES = 2;

/**
 * Open-redirect guard. Resolves the candidate against our own origin and keeps
 * it only if it stays same-origin and is a single-slash absolute path. Routing
 * it through the URL parser (rather than matching the raw string) closes
 * bypasses where the parser rewrites the value before navigation — control-char
 * injection ("/\t/evil.com" -> "//evil.com") and dot-segments
 * ("/..//evil.com" -> pathname "//evil.com"). Anything else falls back to "".
 */
export const sanitizeRedirectPath = (redirect: string): string => {
  if (!redirect) return "";

  try {
    const { origin } = window.location;
    const url = new URL(redirect, origin);

    if (url.origin !== origin) return "";

    const path = `${url.pathname}${url.search}${url.hash}`;

    return path.startsWith("/") && !path.startsWith("//") ? path : "";
  } catch {
    return "";
  }
};

const login = async ({ oktaAuth, redirect = "" }: LoginProps) => {
  const {
    auth: { idp, scopes },
    singleIDPLogin,
  } = config.get();

  const originalUri = `${window.location.origin}${sanitizeRedirectPath(redirect)}`;

  const signInParams: SigninWithRedirectOptions =
    singleIDPLogin && !config.isInstanceManagement()
      ? {
          originalUri,
          idp,
          scopes,
          redirectUri: `${window.location.origin}${routes.loginCallback}`,
        }
      : {
          originalUri,
        };

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      await oktaAuth.signInWithRedirect(signInParams);

      return;
    } catch (error) {
      if (attempt === MAX_RETRIES) {
        throw error;
      }
    }
  }
};

const VIADUCT_EMAIL_SUFFIX = "@viaduct.ai";

// Note that this should ideally match the anonymized email on the API
const ANONYMIZED_EMAIL = "user@anonymous.com";

export const getTrackingEmailFromIDToken = (
  idToken: IDToken
): string | undefined => {
  const { email } = idToken?.claims;
  const {
    featureFlags: { anonymizeEmail },
  } = config.get();

  // if client is configured to anonymize emails for privacy, we send a configured email
  // instead unless it is a viaduct employee.
  if (anonymizeEmail && !email?.endsWith(VIADUCT_EMAIL_SUFFIX)) {
    return ANONYMIZED_EMAIL;
  }

  return email;
};

export default login;
